/* eslint-disable no-underscore-dangle */
import { Logger } from 'vue-infra';
import { each, has, get } from 'lodash';
import {
  RETRY_INTERVAL,
  WIDGET_LOGICAL_NAMES,
  AGENT_STATUSES,
  DATA_LOADING_STATES,
  METRIC_NAMES,
  SORT_DIRECTION,
} from '@config/Consts';
import AgentsListService from '@core/services/agent-list/AgentsListService';
import { AgentsAlertsService } from '@core/services/alerts/agents/AgentsAlertsService';
import { Subscription } from '@core/common/Subscription';
import { isMaxNumRetriesReached, getInterceptor, isEventRelevant } from '@core/utils/utils';
import { ROOT_GETTER_PATHS } from '@core/store';
import {
  RESOURCES_MODEL_MAP,
  RESOURCES_METRICS_MAP,
  AGENT_LIST_RESOURCES,
} from './Consts';

const logger = Logger.getLogger('agentList');

const _createSubscriptionPayload = (getters) => {
  logger.debug('Agent List::actions::_createSubscriptionPayload');
  const sort = (getters.getSort.columnName === METRIC_NAMES.AGENT_VIEW_AGENT_NOTIFICATIONS) ?
    { columnName: METRIC_NAMES.AGENT_VIEW_AGENT_LOAD,
      direction: SORT_DIRECTION.DESCENDING } : getters.getSort;
  return {
    timeframe: getters.getTimeFrameFilter,
    agentIds: getters.getUserIds,
    filter: {
      status: getters.getStatusFilter,
      skills: getters.getSkillsFilter,
      agents: getters.getAgentsFilter,
      groupIds: getters.getFilteredGroupsIds,
    },
    paging: getters.getPaginationConfigForAPI,
    sort,
  };
};

const _getAgentsMetricsData = (responseData, resourceName) => {
  // eslint-disable-next-line prefer-const
  let agentsMetrics = [];

  each(responseData, (agentData) => {
    const agentItem = { id: agentData.id };
    const metrics = [];
    if (agentData && agentData.metrics instanceof RESOURCES_MODEL_MAP[resourceName]) {
      each(RESOURCES_METRICS_MAP[resourceName], (metricName) => {
        metrics.push({ metric: metricName, score: agentData.metrics[metricName] });
      });
      agentItem.metrics = metrics;
    }
    agentsMetrics.push(agentItem);
  });

  return agentsMetrics;
};

const _handleAgentViewResource = ({ commit, getters }, resourceName, responseData) => {
  logger.debug('Agent List::actions::_handleAgentViewResource');
  let dataStatus;
  let agentsData;
  // eslint-disable-next-line prefer-const
  let agentsMetrics = [];

  if (responseData) {
    dataStatus = DATA_LOADING_STATES.SUCCESS;
    agentsData = responseData.data;
    agentsMetrics = _getAgentsMetricsData(agentsData, resourceName);

    if (has(responseData, 'agentsCount')) {
      commit('setPagination', { key: 'totalPages', value: responseData.agentsCount });
    }
  } else if (getters.dataHasBeenFetched) {
    dataStatus = DATA_LOADING_STATES.OBSOLETE;
  } else {
    dataStatus = DATA_LOADING_STATES.ERROR;
  }
  commit('updateAgentsMetrics', { agentsMetrics, resourceName, dataStatus });
};

const _handleMetricsResource = ({ commit, getters }, resourceName, responseData) => {
  logger.debug('Agent List::actions::_handleMetricsResource');
  let dataStatus = DATA_LOADING_STATES.NONE;

  if (responseData) {
    dataStatus = DATA_LOADING_STATES.SUCCESS;
  } else if (getters.dataHasBeenFetched) {
    dataStatus = DATA_LOADING_STATES.OBSOLETE;
  }

  const statusSummary = {
    [AGENT_STATUSES.AWAY]: get(responseData, METRIC_NAMES.AGENT_VIEW_METRICS_AWAY_AGENTS, 0),
    [AGENT_STATUSES.BACK_SOON]:
      get(responseData, METRIC_NAMES.AGENT_VIEW_METRICS_BACK_SOON_AGENTS, 0),
    [AGENT_STATUSES.ONLINE]: get(responseData, METRIC_NAMES.AGENT_VIEW_METRICS_ONLINE_AGENTS, 0),
  };

  commit('setAgentStatusSummary', { statusSummary, dataStatus });
};

const _handleAgentActivityResource = ({ commit, getters }, resourceName, responseData) => {
  logger.debug('Agent List::actions::_handleAgentAvtivityResource');
  let dataStatus = DATA_LOADING_STATES.NONE;
  let agentsMetrics;

  if (responseData) {
    dataStatus = DATA_LOADING_STATES.SUCCESS;
    agentsMetrics = _getAgentsMetricsData(responseData, resourceName);
  } else if (getters.dataHasBeenFetched) {
    dataStatus = DATA_LOADING_STATES.OBSOLETE;
  }

  commit('updateAgentsMetrics', { agentsMetrics, resourceName, dataStatus });
};

const _handleSubscriptionSuccess = (context, resourceName, response) => {
  logger.debug('Agent List::actions::_handleSubscriptionSuccess');
  const { commit, getters, rootGetters, dispatch } = context;
  const responseData = response.data;

  switch (resourceName) {
    case AGENT_LIST_RESOURCES.AGENT_VIEW:
      _handleAgentViewResource({ commit, getters, rootGetters }, resourceName, responseData);
      break;
    case AGENT_LIST_RESOURCES.METRICS:
      _handleMetricsResource({ commit, getters }, resourceName, responseData);
      break;
    case AGENT_LIST_RESOURCES.AGENT_ACTIVITY:
      _handleAgentActivityResource({ commit, getters }, resourceName, responseData);
      break;
    default:
      break;
  }
  dispatch('evaluateMetricsForAlerts');
};

const _onSubscriptionUpdateNotification = (context, resourceName, response) => {
  logger.debug('Agent List::actions::_onSubscriptionUpdateNotification', { resourceName, response });
  _handleSubscriptionSuccess(context, resourceName, response);
};

const _onSubscriptionError = ({ commit, getters, rootGetters }, resourceName, err) => {
  if (!err.cancelled) {
    logger.error('Agent List::actions::_onSubscriptionError', JSON.stringify({ resourceName, err }));
    commit('setDataStatus', { resourceName, status: DATA_LOADING_STATES.ERROR });
  }
  if (isMaxNumRetriesReached(err)) {
    // eslint-disable-next-line no-use-before-define
    setTimeout(() => updateSubscription({ commit, getters, rootGetters }), RETRY_INTERVAL);
  }
};

const _handleSubscriptions = async (context, subscriptions) => {
  const { commit, getters, rootGetters } = context;
  if (subscriptions && subscriptions.subscriptions) {
    each(subscriptions.subscriptions, (subscription) => {
      commit('setDataStatus', { resourceName: subscription.name, status: DATA_LOADING_STATES.LOADING });
      subscription.subscription.clearAllEventListeners();
      subscription.subscription.on(Subscription.EVENTS.onSubscriptionUpdateNotification,
        _onSubscriptionUpdateNotification.bind(this,
          context, subscription.name),
        'agentSubscriptionUpdateNotificationSuccess');
      subscription.subscription.on(Subscription.EVENTS.onSubscriptionError,
        _onSubscriptionError.bind(this, { commit, getters, rootGetters }, subscription.name),
        'agentSubscriptionUpdateNotificationError');
      const subscriptionPayload = _createSubscriptionPayload(getters);
      try {
        logger.debug('Agent List::actions::before executing start subscription');
        subscription.subscription.start({ settings: subscriptionPayload,
          name: subscription.name });
      } catch (ex) {
        logger.error('cannot start Agent List subscription', 'start', { ex, subscription });
      }
    });
  }
};

const startSubscription = async (context) => {
  const { getters, rootGetters } = context;
  logger.debug('Agent List::actions::startSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.AGENTS)) {
    logger.debug('Agent List::no permission to start the agent list widget subscription');
    return;
  }
  const subscriptions = await AgentsListService.getAgentsListSubscriptions({
    interceptor: getInterceptor.bind(this, getters),
  });
  await _handleSubscriptions(context, subscriptions);
};

const stopSubscription = async ({ commit, getters, rootGetters }) => {
  logger.debug('Agent List::actions::stopSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.AGENTS)) {
    logger.debug('Agent List::no permission to stop the agent list widget subscription');
    return;
  }
  const subscriptions = await AgentsListService.getAgentsListSubscriptions({
    interceptor: getInterceptor.bind(this, getters),
  });
  if (subscriptions && subscriptions.subscriptions) {
    each(subscriptions.subscriptions, (subscription) => {
      subscription.subscription.clearAllEventListeners();
      try {
        logger.debug('Agent List::actions::before executing stop subscription');
        subscription.subscription.stop({ name: subscription.name });
        commit('setDataStatus', { resourceName: subscription.name, status: DATA_LOADING_STATES.NONE });
      } catch (ex) {
        logger.error('cannot stop Agent List subscription', 'stop', { ex, subscription });
      }
    });
  }
};

const updateSubscription = async (context, data) => {
  const { commit, getters, rootGetters } = context;
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.AGENTS)) {
    logger.debug('Agent List::no permission to update the agent list subscription');
    return;
  }
  if (data?.resetPageNumber) {
    commit('setPagination', { key: 'currentPage', value: 1 });
  }
  const subscriptions = await AgentsListService.getAgentsListSubscriptions({
    interceptor: getInterceptor.bind(this, getters),
  });
  if (get(subscriptions, 'subscriptions')) {
    each(subscriptions.subscriptions, async (subscription) => {
      const subscriptionPayload = _createSubscriptionPayload(getters);
      try {
        const updateData = { settings: subscriptionPayload, name: subscription.name };
        await subscription.subscription.update(updateData);
      } catch (ex) {
        logger.error('cannot update Agent List subscription', 'update', { ex, subscription });
      }
    });
  }
};

const changeWidgetMode = ({ commit }, mode) => {
  commit('setWidgetMode', mode);
};

const changeStatusFilter = (context, status) => {
  context.commit('setStatusFilter', status);
  return updateSubscription(context);
};

const changeAgentsFilter = (context, agentsFilterList) => {
  context.commit('setAgentsFilter', agentsFilterList);
  const data = { updateAgentViewApi: true };
  return updateSubscription(context, data);
};

// eslint-disable-next-line consistent-return
const changeSort = (context, sort) => {
  context.commit('setSort', sort);
  if (sort.columnName !== METRIC_NAMES.AGENT_VIEW_AGENT_NOTIFICATIONS) {
    return updateSubscription(context);
  }
};

const applyPresetValues = ({ commit }, preset) => {
  commit('setPresetValues', preset);
};

const handleStartSubscription = (context, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.AGENTS, widgets)) {
    startSubscription(context);
  }
};
const handleStopSubscription = (context, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.AGENTS, widgets)) {
    stopSubscription(context);
  }
};
const handleUpdateSubscription = (context, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.AGENTS, widgets)) {
    updateSubscription(context);
  }
};
const handleShowWidgets = (context, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.AGENTS, widgets)) {
    startSubscription(context);
  } else {
    stopSubscription(context);
  }
};

const addAlertToHistory = ({ dispatch }, alertModel) => {
  dispatch('amd/alertsHistory/addAlerts', alertModel, { root: true });
};

const evaluateMetricsForAlerts = async ({ getters, state }) => {
  const alertPermissions = getters.getAlertPermissions;
  const alertData = getters.getAlertData;
  if (alertPermissions.canViewAlert) {
    state.agentsMetrics = await AgentsAlertsService.evaluateMetrics(state.agentsMetrics, alertData);
  }
};

export default {
  RESOURCES_METRICS_MAP,
  changeWidgetMode,
  changeStatusFilter,
  changeAgentsFilter,
  changeSort,
  applyPresetValues,
  handleStartSubscription,
  handleStopSubscription,
  handleUpdateSubscription,
  handleShowWidgets,
  addAlertToHistory,
  updateSubscription, // needed for pagination
  evaluateMetricsForAlerts,
};
